import React from 'react';
import { useField } from 'formik';
import * as Yup from 'yup';
import { Box, FormHelperText, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Close, InsertDriveFileOutlined } from '@mui/icons-material';
import { PdfUploadIcon } from '@uplift/icons';
import { DatePicker } from '../../Inputs/DatePicker';
import { TextInput } from '../../Inputs/TextInput';
import { MaskedInput } from '../../Inputs/MaskedInput';
import { SimpleDropzone } from '../../Dropzone';
import { MAX_PDF_UPLOAD_SIZE } from '@uplift/constants';

export const collabAgreementValidationSchema = Yup.object().shape({
  collaborationAgreement: Yup.mixed().required('Please upload a collaboration agreement'),
  expirationDate: Yup.string().required('Expiration date is required'),
  physicianFirstName: Yup.string().required('Physician first name is required'),
  physicianLastName: Yup.string().required('Physician last name is required'),
  physicianNpi: Yup.string().length(10, 'NPI must be 10 digits').required('NPI is required'),
  physicianEmail: Yup.string().email('Invalid email address').required('Email is required'),
  physicianPhone: Yup.string().nullable(),
});

export type CollabAgreementFormValues = {
  collaborationAgreement: File | null;
  expirationDate: string;
  physicianFirstName: string;
  physicianLastName: string;
  physicianNpi: string;
  physicianEmail: string;
  physicianPhone: string;
};

export const collabAgreementDefaultValues: CollabAgreementFormValues = {
  collaborationAgreement: null,
  expirationDate: '',
  physicianFirstName: '',
  physicianLastName: '',
  physicianNpi: '',
  physicianEmail: '',
  physicianPhone: '',
};

const useDropzoneStyles = makeStyles()(theme => ({
  textContainer: {
    userSelect: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(4, 3),
  },
  icon: {
    fontSize: '48px',
    color: theme.palette.grey[700],
  },
  browseButton: {
    position: 'relative',
    cursor: 'pointer',
    '&:after': {
      content: '""',
      backgroundColor: 'currentColor',
      position: 'absolute',
      width: '100%',
      height: '1px',
      left: 0,
      bottom: '2px',
    },
  },
  fileCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: '4px',
    backgroundColor: theme.palette.grey[50],
    border: `1px solid ${theme.palette.grey[200]}`,
  },
  fileIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[700],
    flexShrink: 0,
  },
  fileIcon: {
    fontSize: 20,
  },
  fileName: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  removeButton: {
    color: theme.palette.grey[700],
  },
}));

const CollabAgreementDropzone: React.VFC<{ name: string }> = ({ name }) => {
  const [ field, meta, helpers ] = useField<File | null>(name);
  const { classes } = useDropzoneStyles();

  if (field.value) {
    return (
      <Box display="flex" flexDirection="column">
        <Box className={classes.fileCard}>
          <Box className={classes.fileIconWrap}>
            <InsertDriveFileOutlined className={classes.fileIcon} />
          </Box>
          <Typography variant="body2" fontWeight={600} className={classes.fileName}>
            {field.value.name}
          </Typography>
          <IconButton
            size="small"
            aria-label="Remove file"
            className={classes.removeButton}
            onClick={() => helpers.setValue(null)}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column">
      <SimpleDropzone
        maxSize={MAX_PDF_UPLOAD_SIZE}
        accept={{ 'application/pdf': [ '.pdf' ] }}
        maxFiles={1}
        onDrop={(accepted) => {
          if (accepted[0]) helpers.setValue(accepted[0]);
        }}
        color="secondary"
        error={Boolean(meta.touched && meta.error)}
      >
        {() => (
          <Box className={classes.textContainer}>
            <PdfUploadIcon className={classes.icon} />
            <Typography color="textPrimary">
              <span style={{ fontWeight: 600 }}>Drag & drop PDF here, or&nbsp;</span>
              <Typography component="span" color="secondary" className={classes.browseButton}>Browse</Typography>
            </Typography>
          </Box>
        )}
      </SimpleDropzone>
      {meta.touched && meta.error && <FormHelperText error={!!meta.error}>{meta.error}</FormHelperText>}
    </Box>
  );
};

const SectionHeader: React.VFC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="secondary" fontWeight={700}>
    {children}
  </Typography>
);

export const CollabAgreementForm: React.VFC = () => {
  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <SectionHeader>Collaborating Physician</SectionHeader>

      <TextInput
        name="physicianFirstName"
        label="First Name"
        fullWidth
        color="secondary"
      />

      <TextInput
        name="physicianLastName"
        label="Last Name"
        fullWidth
        color="secondary"
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MaskedInput
            name="physicianNpi"
            label="NPI Number"
            type="npi"
            fullWidth
            color="secondary"
          />
        </Grid>
      </Grid>

      <TextInput
        name="physicianEmail"
        label="Email"
        fullWidth
        color="secondary"
      />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MaskedInput
            name="physicianPhone"
            label="Phone"
            type="phone"
            fullWidth
            color="secondary"
          />
        </Grid>
      </Grid>

      <SectionHeader>Collaboration Agreement</SectionHeader>

      <CollabAgreementDropzone name="collaborationAgreement" />

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <DatePicker
            name="expirationDate"
            label="Expiration Date"
            fullWidth
            valueFormat="MM/DD/YYYY"
            color="secondary"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
